import * as ImagePicker from "expo-image-picker";

import {
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import React, { useCallback, useEffect, useState } from "react";
import { fp, hp, spH, spV, wp } from "utils/normalizeV2";
import {
  getUserDetails,
  updateUserProfile,
} from "appRedux/reducers/profileSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLocalSearchParams, useRouter } from "expo-router";

import { ANALYTICS_EVENT_NAMES } from "utils/analytics";
import Buttons from "components/Buttons";
import { Colors } from "colors";
import ImageView from "../src/image";
import { Images } from "assets";
import Input from "components/Input";
import ParentScrollView from "components/ParentScrollView";
import ProfileMenuItem from "components/profileMenuItem";
import SideProfileComp from "components/profile/SideProfileComp";
import VText from "components/VText";
import { trackV2 } from "utils/Segment";

const GENDER_DATA = [
  { type: "male", label: "Male" },
  { type: "female", label: "Female" },
  { type: "other", label: "Other" },
];

const EditProfile = ({ onSuccess = () => {}, hideHeader = false }) => {
  const dispatch = useDispatch();

  const router = useRouter();
  const userProfile = useSelector((state) => state.ProfileReducer.profileData);
  const isStylistUser = useSelector((state) => state.AuthReducer.isStylistUser);
  const userId = useSelector((state) => state.AuthReducer.userId);
  const isProfileCreated = useSelector(
    (state) => state.AuthReducer.isProfileCreated
  );

  const [isImageRemove, setIsImageRemove] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [userImage, setUserImage] = useState(null);
  const [gender, setGender] = useState("");
  const [fromLocal, setFromLocal] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const local = useLocalSearchParams();
  const localGender = local?.gender || "female";

  useEffect(() => {
    if (userProfile) {
      setGender(userProfile.gender);
      setUserImage(userProfile.profilePicUrl);
      setName(userProfile.name);
    }
  }, [userProfile]);

  useEffect(() => {
    const isNameChanged = name !== userProfile?.name;
    const isGenderChanged = gender !== userProfile?.gender;
    const isImageChanged = userImage !== userProfile?.profilePicUrl;

    const allRequiredFieldsHaveValues = name !== "" && gender !== null;

    if (allRequiredFieldsHaveValues) {
      setDisabled(!(isNameChanged || isGenderChanged || isImageChanged));
    } else {
      setDisabled(true);
    }
  }, [name, gender, userImage, userProfile]);

  const pickImage = useCallback(async () => {
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 1,
    });

    if (!result.canceled) {
      setUserImage(result?.assets?.[0]?.uri);
      setFromLocal(true);
      setIsImageRemove(false);
    }
  }, []);

  const updateProfile = useCallback(() => {
    if (
      name === userProfile?.name &&
      gender === userProfile?.gender &&
      userImage === userProfile?.profilePicUrl
    ) {
      onSuccess();
      router.push(`${gender == "men" ? "/men" : ""}/profile`);
      return;
    }

    setLoading(true);

    const data = {
      emailId: userProfile?.emailId,
      name: name.trim(),
      gender: gender?.toLowerCase(),
      isImageUpated: fromLocal,
      ...(fromLocal && { base64ImgString: userImage }),
      ...(isProfileCreated &&
        isImageRemove && { base64ImgString: null, isImageUpated: true }),
      ...(isStylistUser ? { personalStylistId: userId } : { userId }),
    };
    dispatch(updateUserProfile(data))
      .then((res) => {
        dispatch(getUserDetails())
          .then((res) => {
            setLoading(false);
            const eventData = {};
            trackV2(ANALYTICS_EVENT_NAMES.PROFILE_UPDATED, eventData);
            router.push(`${gender == "men" ? "/men" : ""}/profile`);
          })
          .catch((err) => {
            setLoading(false);
            console.log("err", err);
            alert("Error");
          });
      })
      .catch((err) => {
        console.log("err", err);
      })
      .finally(() => setLoading(false));
  }, [
    name,
    gender,
    userImage,
    userProfile,
    fromLocal,
    isImageRemove,
    isStylistUser,
    userId,
    isProfileCreated,
    onSuccess,
    router,
  ]);

  return (
    <ParentScrollView>
      <View style={styles.container}>
        <VText style={styles.header}>My Profile</VText>
        <View style={styles.content}>
          <SideProfileComp gender={localGender} selected={"profile"} />
          <View style={styles.formSection}>
            <View style={styles.scrollView}>
              <VText style={styles.sectionTitle}>
                Would you like to upload your picture?
              </VText>
              <View style={styles.imageSection}>
                {userImage ? (
                  <View style={styles.imageContainer}>
                    <ImageView
                      source={{
                        uri: fromLocal ? userImage : userImage,
                        priority: ImageView.priority.high,
                      }}
                      style={styles.userImage}
                    />
                    <View style={styles.imageButtonsContainer}>
                      <Buttons
                        text="Change"
                        isInverse
                        onPress={pickImage}
                        containerStyle={styles.uploadButton}
                      />
                      <Buttons
                        text="Remove"
                        isInverse
                        onPress={() => {
                          setIsImageRemove(true);
                          setUserImage(null);
                        }}
                        containerStyle={styles.uploadButton}
                      />
                    </View>
                  </View>
                ) : (
                  <>
                    <ImageView
                      source={Images.iProfile}
                      style={styles.defaultImage}
                    />
                    <Buttons
                      text="Upload"
                      isInverse
                      textStyle={{ color: Colors.black }}
                      onPress={pickImage}
                      containerStyle={styles.uploadButton}
                    />
                  </>
                )}
              </View>
              <View style={{ gap: 8 }}>
                <VText style={styles.inputLabel}>Please enter your name</VText>
                <Input
                  containerStyle={{
                    width: spH(316),
                    backgroundColor: "white",
                    height: hp(40),
                  }}
                  textInputProps={{
                    returnKeyType: "next",
                    height: hp(40),
                  }}
                  inputContainerStyle={{
                    height: hp(40),
                  }}
                  placeholder="Name"
                  propStyle={styles.input}
                  onChangeText={(e) => {
                    setName(e.replace(/^\s+/, "").replace(/\d+/g, ""));
                  }}
                  value={name}
                />
                {/* <VText style={styles.inputHelper}>
                                You can change this later in your profile settings.
                            </VText> */}
              </View>
              <View style={styles.genderSection}>
                <VText style={styles.inputLabel}>What's your gender?</VText>
                <View style={styles.genderButtonsContainer}>
                  {GENDER_DATA.map((item, index) => (
                    <TouchableOpacity
                      key={item.type}
                      style={[
                        styles.genderButton,
                        item.type === gender && styles.genderButtonActive,
                        index === 1 && styles.genderButtonMiddle,
                      ]}
                      onPress={() => setGender(item.type)}
                    >
                      <VText style={styles.genderButtonText}>
                        {item.label}
                      </VText>
                    </TouchableOpacity>
                  ))}
                </View>
                <VText style={styles.inputHelper}>
                  Feeds will be prioritised based on your gender.
                </VText>
              </View>
              <Buttons
                loading={loading}
                text="Save Changes"
                disabled={disabled}
                onPress={updateProfile}
                containerStyle={[
                  styles.doneButton,
                  { opacity: disabled ? 0.3 : 1 },
                ]}
              />
            </View>
          </View>
        </View>
      </View>
    </ParentScrollView>
  );
};

export default EditProfile;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "white",
    paddingTop: spV(32),
  },
  header: {
    textAlign: "center",
    fontSize: fp(28),
    fontFamily: "LibreBodoni",
    fontWeight: "350",
    paddingBottom: spV(28),
  },
  content: {
    flex: 1,
    flexDirection: "row",
  },
  formSection: {
    marginHorizontal: spH(180),
    backgroundColor: Colors.grey1,
    padding: spV(40),
  },
  scrollView: {
    backgroundColor: Colors.grey1,
    maxHeight: 700,
  },
  sectionTitle: {
    fontFamily: "JosefinSans",
    fontSize: 18,
    lineHeight: 28,
    fontWeight: "600",
  },
  imageSection: {
    alignItems: "flex-start",
    marginBottom: 24,
  },
  imageContainer: {},
  userImage: {
    width: spH(60),
    height: spH(60),
    borderRadius: spH(30),
    marginTop: spV(15),
  },
  defaultImage: {
    width: spH(60),
    height: spH(60),
    borderRadius: spH(30),
    marginTop: spV(14),
  },
  imageButtonsContainer: {
    gap: 10,
    marginTop: spV(10),
    flexDirection: "row",
    justifyContent: "center",
  },
  imageButton: {
    padding: 12,
    marginHorizontal: 8,
  },
  uploadButton: {
    borderWidth: 1,
    paddingHorizontal: 16,
    height: hp(36),
    borderColor: Colors.greyBorder,
    backgroundColor: Colors.grey1,
    marginTop: spV(12),
  },
  inputLabel: {
    fontFamily: "JosefinSans",
    fontSize: 18,
    lineHeight: 28,
    fontWeight: "600",
  },
  input: {
    width: "100%",
  },
  inputHelper: {
    fontFamily: "JosefinSans",
    fontSize: 15,
    lineHeight: 24,
    fontWeight: "350",
    marginTop: 5,
    color: Colors.black60,
  },
  genderSection: {
    marginTop: 24,
  },
  genderButtonsContainer: {
    flexDirection: "row",
    marginVertical: spV(12),
  },
  genderButton: {
    paddingVertical: spV(8),
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: Colors.greyBorder,
    alignItems: "center",
    width: wp(100),
  },
  genderButtonActive: {
    borderWidth: 1,
    borderColor: Colors.black,
  },
  genderButtonMiddle: {
    marginHorizontal: 8,
  },
  genderButtonText: {
    textTransform: "capitalize",
  },
  footer: {
    position: "absolute",
    bottom: 10,
    left: 0,
    right: 0,
    paddingHorizontal: 16,
  },
  doneButton: {
    width: spH(316),
    height: spV(38),
    marginTop: spV(25),
  },
});
